import {
    filter, findKey, first, isNull, last, nth,
} from 'lodash-es';
import constant from './constant';

const inflation = 0.0234;
const defaultCorelation = 0.15;
const monthsInYear = 12;
const loanMultiply = 1.5;
const defaultPayAsYouEarn = 0.1;
export default {
    calculatePayment(balance, rate, term) {
        return balance * (rate / (monthsInYear * 100) / (1 - (1 + rate / (monthsInYear * 100)) ** (term * monthsInYear * -1)));
    },
    round(value, digitCount) {
        const ndigits = digitCount !== undefined ? digitCount : 2;
        return value.toFixed(ndigits);
    },
    standardPeriodCalculation(rate, payment, present) {
        const future = 0;
        const num = payment - future * rate;
        const denominator = payment + present * rate;
        return Math.floor(Math.log(num / denominator) / Math.log(1 + rate));
    },
    getIncomeByFamilySize(familySize, averageIncome, incomeDifference) {
        return averageIncome + familySize * incomeDifference;
    },
    povertyLevel(state, familySize) {
        if (state === 3) {
            const averageIncome = 10200;
            const incomeDifference = 5430;
            return this.getIncomeByFamilySize(familySize, averageIncome, incomeDifference);
        }
        if (state === 2) {
            const averageIncome = 11090;
            const incomeDifference = 5900;
            return this.getIncomeByFamilySize(familySize, averageIncome, incomeDifference);
        }
        if (state === 1) {
            const averageIncome = 8870;
            const incomeDifference = 4720;
            return this.getIncomeByFamilySize(familySize, averageIncome, incomeDifference);
        }
        return '';
    },
    standardPaymentCalculation(rate, numberOfPeriod, presentValue) {
        if (rate === 0) {
            return -presentValue / numberOfPeriod;
        }
        const presentValueOfRate = (1 + rate) ** numberOfPeriod;
        const result = rate / (presentValueOfRate - 1) * -(presentValue * presentValueOfRate);
        return -result;
    },
    incomeFactor(averageGrossIncome, standardPayment, married, numOfMonths = 0) {
        let incomes;
        let percentages;
        let months = numOfMonths;
        if (months === null) {
            months = 0;
        }
        if (married === 'yes') {
            incomes = [...constant.marriedIncome].reverse();
            percentages = [...constant.marriedPercentages].reverse();
        } else {
            incomes = [...constant.unMarriedIncome].reverse();
            percentages = [...constant.unMarriedPercentages].reverse();
        }
        const corelation = (1 + inflation) ** Math.floor(months / monthsInYear);
        if (averageGrossIncome > first(incomes) * corelation) {
            return standardPayment * 2;
        }
        let incomeIndex = null;
        for (let index = 0; index < incomes.length; index++) {
            const income = incomes[index];
            if (averageGrossIncome > income * corelation && isNull(incomeIndex)) {
                incomeIndex = index;
            }
        }
        if (isNull(incomeIndex)) {
            incomeIndex = incomes.length - 1;
        }
        const avgGrossIncome = averageGrossIncome - incomes[incomeIndex] * corelation;
        const avgIncome = incomes[incomeIndex - 1] * corelation - incomes[incomeIndex] * corelation;
        const avgPercentage = percentages[incomeIndex - 1] - percentages[incomeIndex];
        return standardPayment * (avgGrossIncome / avgIncome * avgPercentage / 100 + percentages[incomeIndex] / 100);
    },
    countFold(totalMonths, startingMonth) {
        let count = 0;
        if (totalMonths !== 0 && startingMonth !== 0 && totalMonths >= startingMonth) {
            let monthCount = startingMonth;
            while (monthCount <= totalMonths) {
                if (monthCount % startingMonth === 0) {
                    count++;
                }
                monthCount++;
            }
        }
        return totalMonths / count === startingMonth ? count : false;
    },
    updateParamByYear(value, month, percentage) {
        if (this.countFold(month - 1, monthsInYear) !== false) {
            return this.round(value * (1 + percentage), 2);
        }
        return value;
    },
    makeIBRMonthData(data, month) {
        let paymentTowardsPrincipal;
        if (data.payment > data.loanPrincipalBalance) {
            paymentTowardsPrincipal = data.loanPrincipalBalance;
        } else if (data.totalInterestAccumulated === 0) {
            paymentTowardsPrincipal = data.payment - data.monthlyInterest;
        } else {
            paymentTowardsPrincipal = data.totalInterestAccumulated > data.payment - data.monthlyInterest
                ? 0
                : data.payment - data.monthlyInterest - data.totalInterestAccumulated;
        }
        const paymentTowardsInterest = data.payment < data.monthlyInterest
            ? data.payment
            : data.totalInterestAccumulated > data.payment - data.monthlyInterest ? data.payment : data.totalInterestAccumulated + data.monthlyInterest;
        return {
            averageGrossIncome: data.averageGrossIncome,
            houseHoldIncome: data.houseHoldIncome,
            loanPrincipalBalance: data.loanPrincipalBalance,
            month,
            monthlyInterest: data.monthlyInterest,
            payment: data.payment,
            paymentTowardsInterest,
            paymentTowardsPrincipal,
            totalAmountPaid: data.totalAmountPaid,
            totalInterestAccumulated: data.totalInterestAccumulated,
        };
    },
    /**
     * Based on initial input from user, calculate years of Income Based Reapayment
     * invoked with one arguments: (data).
     *
     * @param {any} data made object that pass initial input from user and some initial parameter that need to pass for calculation.
     * @returns {object} Returns months Object
    */
    calculateIBRYears(data) {
        const months = {};
        const corelation = data.corelation !== undefined ? data.corelation : defaultCorelation;
        const ibrYears = true;

        let month = 1;
        while (month <= data.numOfMonths) {
            const objectPayment = month === 1
                ? this.calculateFirstMonthPayment(data.averageGrossIncome, data.houseHoldIncome, data.loanPrincipalBalance, data.weightedInterestRate, ibrYears, data.standardPayment, corelation)
                : this.calculateAllMonthsPayment(months, month, data.inflation, data.annualIncomeGrowth, data.standardPayment, data.weightedInterestRate, ibrYears, corelation);
            months[month] = this.makeIBRMonthData(objectPayment, month);
            month++;
        }
        return months;
    },
    /**
     * Based on initial input from user, calculate years of Revised Pay As You Earn
     * invoked with one arguments: (data).
     *
     * @param {any} data made object that pass initial input from user and some initial parameter that need to pass for calculation.
     * @returns {object} Returns months Object
    */
    calculateRepayeYears(data) {
        const months = {};
        const ibrYears = false;

        let month = 1;
        while (month <= data.numberOfMonths) {
            const objectPayment = month === 1
                ? this.calculateFirstMonthPayment(data.averageGrossIncome, data.houseHoldIncome, data.loanPrincipalBalance, data.weightedInterestRate, ibrYears)
                : this.calculateAllMonthsPayment(months, month, data.inflation, data.annualIncomeGrowth, data.standardPayment, data.weightedInterestRate, ibrYears);
            months[month] = this.makeIBRMonthData(objectPayment);
            month++;
        }
        return months;
    },
    calculateFirstMonthPayment(averageGrossIncome, houseHoldIncome, loanPrincipalBalance, weightedInterestRate, ibrYears, standardPayment, corelation) {
        let payment = '';
        if (ibrYears === true) {
            payment = Math.max(0, Math.min((averageGrossIncome - houseHoldIncome * loanMultiply) * corelation / monthsInYear, standardPayment));
        } else {
            payment = Math.max(0, Math.min((averageGrossIncome - houseHoldIncome * loanMultiply) * defaultPayAsYouEarn / monthsInYear, loanPrincipalBalance));
        }
        const monthlyInterest = loanPrincipalBalance * weightedInterestRate / monthsInYear;
        let totalInterestAccumulated = '';
        if (ibrYears === true) {
            totalInterestAccumulated = payment - monthlyInterest > 0 ? 0 : monthlyInterest - payment;
        } else {
            totalInterestAccumulated = payment - monthlyInterest > 0 ? 0 : (monthlyInterest - payment) / 2;
        }
        const totalAmountPaid = payment;
        return {
            payment,
            monthlyInterest,
            totalInterestAccumulated,
            totalAmountPaid,
            averageGrossIncome,
            houseHoldIncome,
            loanPrincipalBalance,
        };
    },
    calculateAllMonthsPayment(months, month, inflationValue, annualIncomeGrowthValue, standardPaymentValue, weightedInterestRate, ibrYears, corelation) {
        const previousMonth = months[month - 1];
        const previousHouseHoldIncome = previousMonth.houseHoldIncome;
        const previousAverageGrossIncome = previousMonth.averageGrossIncome;
        const previousLoanBalance = previousMonth.loanPrincipalBalance;
        const previousMonthlyInterest = previousMonth.monthlyInterest;
        const previousPayment = previousMonth.payment;
        const prePayment = previousLoanBalance + previousMonthlyInterest - previousPayment + (previousLoanBalance + previousMonthlyInterest - previousPayment) * weightedInterestRate / monthsInYear;
        const previousPaymentTowardsPrincipal = previousMonth.paymentTowardsPrincipal;
        const previousPaymentTowardsInterest = previousMonth.paymentTowardsInterest;
        const previousTotalInterestAccumulated = previousMonth.totalInterestAccumulated;
        const previousTotalAmountPaid = previousMonth.totalAmountPaid;

        const houseHoldIncome = this.updateParamByYear(previousHouseHoldIncome, month, inflationValue);
        const averageGrossIncome = this.updateParamByYear(previousAverageGrossIncome, month, annualIncomeGrowthValue);

        const firstPaymentVariant = prePayment < 0.01 ? this.round(prePayment, 2) : prePayment;
        const secondPaymentVariant = (averageGrossIncome - houseHoldIncome * loanMultiply) * defaultPayAsYouEarn / monthsInYear;
        let payment = '';
        if (ibrYears === true) {
            const applicableIncomeForLoan = averageGrossIncome - houseHoldIncome * loanMultiply;
            const applicableMinimumIncome = Math.min(applicableIncomeForLoan * corelation / monthsInYear, standardPaymentValue);
            const prePaymentForMinPaymnet = prePayment < 0.01 ? Math.trunc(prePayment) : prePayment;
            const minPayment = Math.min(prePaymentForMinPaymnet, applicableMinimumIncome);
            payment = Math.max(0, minPayment);
        } else {
            payment = Math.max(0, Math.min(firstPaymentVariant, secondPaymentVariant));
        }

        let loanPrincipalBalance = '';
        let monthlyInterest = '';
        if (payment !== standardPaymentValue) {
            loanPrincipalBalance = previousLoanBalance - previousPaymentTowardsPrincipal;
        } else {
            loanPrincipalBalance = previousLoanBalance - previousPaymentTowardsPrincipal + previousTotalInterestAccumulated;
        }
        if (previousLoanBalance < loanPrincipalBalance) {
            monthlyInterest = (previousLoanBalance - previousPaymentTowardsPrincipal) * weightedInterestRate / monthsInYear;
        } else {
            monthlyInterest = loanPrincipalBalance * weightedInterestRate / monthsInYear;
        }
        let totalInterestAccumulated = '';
        if (ibrYears === true) {
            totalInterestAccumulated = Math.max(monthlyInterest - previousPaymentTowardsInterest + previousTotalInterestAccumulated, 0);
        } else {
            const maxAccumulated = Math.max(monthlyInterest - previousPaymentTowardsInterest + previousTotalInterestAccumulated, 0);
            totalInterestAccumulated = previousMonthlyInterest > previousPayment
                ? (previousMonthlyInterest - previousPayment) / 2 + previousTotalInterestAccumulated
                : maxAccumulated;
        }
        const totalAmountPaid = previousTotalAmountPaid + payment;

        return {
            houseHoldIncome,
            averageGrossIncome,
            payment,
            loanPrincipalBalance,
            monthlyInterest,
            totalInterestAccumulated,
            totalAmountPaid,
        };
    },
    /**
     * Based on initial input from user, calculate standard payment calculation
     * invoked with one arguments: (data).
     *
     * @param {any} data made object that pass intial input from user for calculation.
     * @returns {object} Returns object for standard calculation
     */

    getStandardCalculation(data) {
        const standardPayment = this.calculatePayment(data.totalDebt, data.rate, 10);
        if (!data.rate > 0) {
            return {
                totalForgiveness: 0,
            };
        }
        return {
            firstMonthPayment: this.round(standardPayment),
            finalPayment: this.round(standardPayment),
            totalAmountPaid: standardPayment * 120,
            totalForgiveness: 0,
            repaymentPeriod: 120,
        };
    },
    /**
     * Based on initial input from user, calculate Income Based Reapayment ( IBR ) Calculation
     * invoked with one arguments: (data).
     *
     * @param {any} data made object that pass initial input from user for calculation.
     * @returns {object} Returns object IBR calculation
     */
    getIncomeBasedRepaymentCalculation(data) {
        const rateDecimals = data.rate / 100.0;
        const poverty = this.povertyLevel(data.state, data.familySize);
        const annualIncomeGrowth = data.annualIncomeGrowth / 100;
        const totalMonths = data.disbursed * monthsInYear;
        const term = 10;
        const standardPayment = this.calculatePayment(data.totalDebt, data.rate, term);
        const corelation = data.corelationOfIncomeBasedRepayment || data.corelationForPayAsOn;
        const dataIBR = {
            numOfMonths: totalMonths,
            houseHoldIncome: poverty,
            averageGrossIncome: data.adjustedGrowthIncome,
            loanPrincipalBalance: data.totalDebt,
            annualIncomeGrowth,
            inflation,
            weightedInterestRate: rateDecimals,
            standardPayment,
            corelation,
        };
        const calculatedPayments = this.calculateIBRYears(dataIBR);
        let finalPayment = 0;
        let totalMonthsWithPayments = totalMonths;
        let message = null;
        const firstMonthPayment = calculatedPayments[1].payment;
        if (firstMonthPayment < standardPayment) {
            if (calculatedPayments[totalMonths].payment === 0) {
                const firstMonthPaymentZero = findKey(calculatedPayments, cp => cp.payment === 0);
                if (filter(calculatedPayments, cp => cp.payment > 0).length === 0) {
                    finalPayment = calculatedPayments[totalMonths].payment;
                    totalMonthsWithPayments = 0;
                } else {
                    finalPayment = calculatedPayments[firstMonthPaymentZero - 2].payment;
                    totalMonthsWithPayments = firstMonthPaymentZero - 1;
                }
            } else {
                finalPayment = Math.max(calculatedPayments[totalMonths].payment, calculatedPayments[totalMonths - 1].payment);
                totalMonthsWithPayments = totalMonths;
            }
            const { totalAmountPaid } = calculatedPayments[totalMonths];
            const finalLoanBalance = calculatedPayments[totalMonths].loanPrincipalBalance;
            const finalPaymentTowardsPrincipal = calculatedPayments[totalMonths].paymentTowardsPrincipal;
            const finalTotalInterestAccumulated = calculatedPayments[totalMonths].totalInterestAccumulated;
            let finalLoanPrincipalBalance = 0;
            if (finalPayment !== standardPayment) {
                const finalPaymentRatio = calculatedPayments[totalMonths].paymentTowardsInterest;
                finalLoanPrincipalBalance = finalPayment === finalPaymentRatio ? finalLoanBalance : finalLoanBalance - finalPaymentTowardsPrincipal;
            } else {
                finalLoanPrincipalBalance = finalLoanBalance - finalPaymentTowardsPrincipal + finalTotalInterestAccumulated;
            }
            const totalForgiveness = Math.max(finalLoanPrincipalBalance + finalTotalInterestAccumulated, 0);
            let standardPeriod = this.standardPeriodCalculation(data.rate / 100 / monthsInYear, 0 - data.monthlyPayment, data.totalDebt);
            if (!standardPeriod) {
                standardPeriod = 600;
                message = constant.errorMessages.standardMessage;
            }
            return {
                firstMonthPayment: this.round(firstMonthPayment),
                finalPayment: this.round(finalPayment),
                totalAmountPaid: this.round(totalAmountPaid),
                totalForgiveness: this.round(totalForgiveness),
                repaymentPeriod: totalMonthsWithPayments,
                message,
            };
        }
        return {
            message: constant.errorMessages.defaultIBRMessage,
        };
    },
    /**
     * Based on initial input from user, calculate Revised Pay As You Earn ( REPAYE ) Calculation
     * invoked with one arguments: (data).
     *
     * @param {any} data made object that pass intial input from user for calculation.
     * @returns {object} Returns object for REPAYE calculation
     */
    getRepaymentAsOnCalculation(data) {
        let finalLoanPrincipalBalance;
        let firstMonthPaymentZero;
        let message = null;
        const rateDecimals = data.rate / 100.0;
        const poverty = this.povertyLevel(data.state || 1, data.familySize);
        const annualIncomeGrowth = data.annualIncomeGrowth / 100;
        const totalMonths = data.disbursed * monthsInYear;
        const standardPayment = this.calculatePayment(data.totalDebt, data.rate, 10);
        const corelation = data.corelationOfIncomeBasedRepayment || data.corelationForPayAsOn || data.repayForCorelation || defaultCorelation;
        const dataRepaye = {
            numberOfMonths: totalMonths,
            houseHoldIncome: poverty,
            averageGrossIncome: data.adjustedGrowthIncome,
            loanPrincipalBalance: data.totalDebt,
            annualIncomeGrowth,
            inflation,
            weightedInterestRate: rateDecimals,
            standardPayment,
            graduateSchool: data.graduateSchool,
            corelation,
        };
        const calculatedPayments = this.calculateRepayeYears(dataRepaye);
        let finalPayment = 0;
        let totalMonthsWithPayments = totalMonths;
        const firstMonthPayment = calculatedPayments[1].payment;
        if ((calculatedPayments[totalMonths].payment || 0) === 0) {
            firstMonthPaymentZero = findKey(calculatedPayments, cp => (cp.payment || 0) === 0);
            if (filter(calculatedPayments, cp => cp.payment > 0).length === 0) {
                finalPayment = calculatedPayments[totalMonths].payment;
                totalMonthsWithPayments = 0;
            } else {
                if (calculatedPayments[firstMonthPaymentZero - 2] !== undefined) {
                    finalPayment = calculatedPayments[firstMonthPaymentZero - 2].payment;
                }
                totalMonthsWithPayments = firstMonthPaymentZero - 1;
            }
        } else {
            if (calculatedPayments[totalMonths].payment / calculatedPayments[totalMonths - 1].payment < 0.7) {
                finalPayment = calculatedPayments[totalMonths - 1].payment;
            } else {
                finalPayment = calculatedPayments[totalMonths].payment;
            }
            totalMonthsWithPayments = totalMonths;
        }
        let { totalAmountPaid } = calculatedPayments[totalMonths];
        if ((totalAmountPaid || 0) === 0 && firstMonthPaymentZero && calculatedPayments[firstMonthPaymentZero - 1]) {
            totalAmountPaid = calculatedPayments[firstMonthPaymentZero - 1].totalAmountPaid;
        }
        const finalLoanBalance = calculatedPayments[totalMonths - 1].loanPrincipalBalance;
        const finalPaymentTowardsPrincipal = calculatedPayments[totalMonths].paymentTowardsPrincipal;
        const finalTotalInterestAccumulated = calculatedPayments[totalMonths].totalInterestAccumulated;
        if (finalPayment !== standardPayment) {
            const finalPaymentRatio = calculatedPayments[totalMonths].paymentTowardsInterest;
            finalLoanPrincipalBalance = finalPayment === finalPaymentRatio ? finalLoanBalance : finalLoanBalance - finalPaymentTowardsPrincipal;
        } else {
            finalLoanPrincipalBalance = finalLoanBalance - finalPaymentTowardsPrincipal + finalTotalInterestAccumulated;
        }
        if (Math.abs(finalPayment - finalLoanPrincipalBalance) < 10) {
            finalLoanPrincipalBalance = 0;
        }
        const totalForgiveness = Math.max(finalLoanPrincipalBalance + finalTotalInterestAccumulated, 0) || 0;
        const standardPeriod = this.standardPeriodCalculation(data.rate / 100 / monthsInYear, 0 - data.monthlyPayment, data.totalDebt) || 0;
        if (standardPeriod === 0) {
            message = constant.errorMessages.standardMessage;
        }
        return {
            firstMonthPayment: this.round(firstMonthPayment),
            finalPayment: this.round(finalPayment),
            totalAmountPaid: this.round(totalAmountPaid),
            totalForgiveness: this.round(totalForgiveness),
            repaymentPeriod: totalMonthsWithPayments,
            message,
        };
    },
    /**
     * Based on initial input from user, calculate Income-Contingent Repayment ( ICR ) Calculation
     * invoked with one argument: (data).
     *
     * @param {any} data made object that pass initial input from user for calculation.
     * @returns {object} Returns object for ICR ( IncomeContingentRepayment ) calculation
     */
    getIncomeContingentRepaymentCalculation(data) {
        let message = null;
        const objectMonth = this.makeICRMonthsData(data);
        const totalForgiveness = this.totalForgivenessCalculation(objectMonth.results, objectMonth.totalMonths, objectMonth.icr);
        let standardPeriod = this.standardPeriodCalculation(data.rate / 100 / monthsInYear, 0 - data.monthlyPayment, data.totalDebt);
        if (!standardPeriod) {
            standardPeriod = 600;
            message = constant.errorMessages.standardMessage;
        }
        return {
            firstMonthPayment: this.round(objectMonth.firstMonthPayment),
            finalPayment: this.round(nth(objectMonth.results, -2).payment),
            totalAmountPaid: this.round(last(objectMonth.results).totalAmountPaid),
            totalForgiveness: this.round(totalForgiveness),
            repaymentPeriod: last(objectMonth.results).month,
            message,
        };
    },
    makeICRMonthsData(data) {
        let payment;
        let totalAmountPaid;
        let totalDebt;
        let totalInterestAccumulated;
        let totalMonths;
        const results = [];
        let icr = null;
        let isLoopContinue = true;
        let monthsCount = 1;
        if (data.disbursed) {
            totalMonths = data.disbursed * monthsInYear;
        }
        const avgRate = data.rate / 100.0;
        const maxCapitalizedInterest = data.totalDebt * defaultPayAsYouEarn;
        const annualIncomeGrowth = data.annualIncomeGrowth / 100;
        let houseHoldIncome = this.povertyLevel(data.state, data.familySize);
        const standardPayment = this.standardPaymentCalculation(avgRate / monthsInYear, 144, data.totalDebt);
        let adjustedPayment = this.incomeFactor(data.adjustedGrowthIncome, standardPayment, data.married);
        const firstMonthPayment = Math.max(0, Math.min(data.totalDebt, Math.min((data.adjustedGrowthIncome - houseHoldIncome) * 0.2 / monthsInYear, adjustedPayment)));
        let capitalizedInterestTotal = 0;
        let capitalizedInterest = 0;
        while (isLoopContinue) {
            let adjustedGrowthIncome;
            let monthlyInterest;
            if (icr) {
                const allMonthData = {
                    results,
                    monthsCount,
                    annualIncomeGrowth,
                    standardPayment,
                    married: data.married,
                    avgRate,
                    maxCapitalizedInterest,
                    capitalizedInterestTotal,
                    capitalizedInterest,
                };
                const objectPayment = this.makeICRAllMonthsData(allMonthData);
                adjustedGrowthIncome = objectPayment.adjustedGrowthIncome;
                adjustedPayment = objectPayment.adjustedPayment;
                houseHoldIncome = objectPayment.houseHoldIncome;
                payment = objectPayment.payment;
                totalDebt = objectPayment.totalDebt;
                monthlyInterest = objectPayment.monthlyInterest;
                totalInterestAccumulated = objectPayment.totalInterestAccumulated;
                totalAmountPaid = objectPayment.totalAmountPaid;
                capitalizedInterestTotal = objectPayment.capitalizedInterestTotal;
                capitalizedInterest = objectPayment.capitalizedInterest;
                if (payment === 0) {
                    isLoopContinue = false;
                }
            } else {
                payment = firstMonthPayment;
                icr = payment;
                totalDebt = data.totalDebt;
                monthlyInterest = totalDebt * avgRate / monthsInYear;
                adjustedGrowthIncome = data.adjustedGrowthIncome;
                totalInterestAccumulated = payment - monthlyInterest > 0 ? 0 : monthlyInterest - payment;
                totalAmountPaid = payment;
            }
            const paymentPrincipal = this.makeICRInterestData(payment, monthlyInterest, totalInterestAccumulated, totalDebt);
            if (isLoopContinue === true) {
                results.push({
                    month: monthsCount,
                    adjustedGrowthIncome,
                    payment,
                    houseHoldIncome,
                    totalDebt,
                    monthlyInterest,
                    totalInterestAccumulated,
                    paymentTowardsPrincipal: paymentPrincipal.paymentTowardsPrincipal,
                    paymentTowardsInterest: paymentPrincipal.paymentTowardsInterest,
                    totalAmountPaid,
                    adjustedPayment,
                });
            }
            if (monthsCount === totalMonths) {
                isLoopContinue = false;
            }
            monthsCount++;
        }
        return {
            results,
            firstMonthPayment,
            totalMonths,
            icr,
        };
    },
    makeICRInterestData(payment, monthlyInterest, totalInterestAccumulated, totalDebt) {
        let paymentTowardsPrincipal;
        const paymentTowardsInterest = payment < monthlyInterest
            ? payment
            : totalInterestAccumulated > payment - monthlyInterest
                ? payment
                : totalInterestAccumulated + monthlyInterest;
        if (payment > totalDebt) {
            paymentTowardsPrincipal = totalDebt;
        } else if (totalInterestAccumulated > 0) {
            if (totalInterestAccumulated > payment - monthlyInterest) {
                paymentTowardsPrincipal = 0;
            } else {
                paymentTowardsPrincipal = payment - monthlyInterest - totalInterestAccumulated;
            }
        } else {
            paymentTowardsPrincipal = payment - monthlyInterest;
        }
        return {
            paymentTowardsInterest,
            paymentTowardsPrincipal,
        };
    },
    makeICRAllMonthsData(data) {
        let { capitalizedInterestTotal, capitalizedInterest } = data;
        const prevStep = last(data.results);
        const yearEnd = data.monthsCount % monthsInYear === 1;
        const adjustedGrowthIncome = yearEnd ? prevStep.adjustedGrowthIncome * (1 + data.annualIncomeGrowth) : prevStep.adjustedGrowthIncome;
        const adjustedPayment = this.incomeFactor(adjustedGrowthIncome, data.standardPayment, data.married, data.monthsCount);
        const houseHoldIncome = yearEnd ? prevStep.houseHoldIncome * (1 + inflation) : prevStep.houseHoldIncome;
        const minTotalDebt = (prevStep.totalDebt + prevStep.monthlyInterest - prevStep.payment) * (1 + data.avgRate / monthsInYear);
        const minAdjustPayment = Math.min(adjustedPayment, (adjustedGrowthIncome - houseHoldIncome) * 0.2 / monthsInYear);
        const payment = Math.max(0, Math.min(this.round(minTotalDebt), minAdjustPayment));
        let totalDebt = payment === data.standardPayment
            ? prevStep.totalDebt - prevStep.paymentTowardsPrincipal + prevStep.totalInterestAccumulated
            : prevStep.totalDebt - prevStep.paymentTowardsPrincipal;

        if (yearEnd) {
            if (capitalizedInterestTotal < data.maxCapitalizedInterest) {
                totalDebt += capitalizedInterest;
            }
            capitalizedInterest = 0;
        }
        const monthlyInterest = prevStep.totalDebt < totalDebt
            ? (prevStep.totalDebt - prevStep.paymentTowardsPrincipal) * data.avgRate / monthsInYear
            : totalDebt * data.avgRate / monthsInYear;
        if (payment < monthlyInterest) {
            capitalizedInterest += monthlyInterest - payment;
            capitalizedInterestTotal += monthlyInterest - payment;
        }
        const totalInterestAccumulated = Math.max(monthlyInterest - prevStep.paymentTowardsInterest + prevStep.totalInterestAccumulated, 0);
        const totalAmountPaid = payment + prevStep.totalAmountPaid;
        return {
            adjustedGrowthIncome,
            adjustedPayment,
            houseHoldIncome,
            payment,
            totalDebt,
            monthlyInterest,
            totalInterestAccumulated,
            totalAmountPaid,
            capitalizedInterestTotal,
            capitalizedInterest,
        };
    },
    totalForgivenessCalculation(results, totalMonths, icr) {
        if (last(results).month === totalMonths) {
            let First = '';
            if (last(results).payment === icr) {
                First = last(results).totalDebt - last(results).paymentTowardsInterest + last(results).totalInterestAccumulated;
            } else if (Math.round(last(results).payment) === Math.round(last(results).paymentTowardsInterest)) {
                First = last(results).totalDebt;
            } else {
                First = last(results).totalDebt - last(results).paymentTowardsInterest;
            }
            return First + last(results).totalInterestAccumulated;
        }
        return 0;
    },
};
